import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import {
	IItemForGetShortInfo,
	TFileId,
	IServerError,
	IUserEmailValidationError,
	IGetUserInfo,
	IUpdateUser,
} from 'types';
import {
	editUserInitialState,
	TEditUserSelectedItem,
	TTypeOfEditUserModalWindow,
	EditUserContext,
	TSourceUrlLocally,
} from './editUserContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditUserActionTypes } from './editUserActions';
import { editUserReducer } from './editUserReducer';
import { useFetchData } from 'hooks/useFetchData';
import { getUserInfo } from 'backend/users';

const EditUserState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editUserReducer, editUserInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const userID = params.get('userID');

	const { response, loading, refetch } = useFetchData<IGetUserInfo, string>(
		userID ? getUserInfo : null,
		userID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditUserActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const fullName = response ? `${response?.data.user.firstName} ${response?.data.user.surName}` : undefined;

	useNamedHeader(fullName);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditUserActionTypes.SET_ALL_DATA,
				payload: {
					filePath: response.data.user.filePath,
					userID: response.data.user.id,
					name: response.data.user.firstName,
					surName: response.data.user.surName,
					email: response.data.user.email,
					phoneNumber: response.data.user.phoneNumber,
					selectedRole: response.data.user.roleId,
					selectedCustomer: response.data.user.customer?.id || '',
					selectedCustomers: response.data.user.customers?.map((el) => el.id) || [],
					initialIDsSelectedRestaurant: response.data.user.restaurants?.map((el) => el.id) || [],
				},
			});
			dispatch({
				type: EditUserActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.user.firstName,
						surName: response.data.user.surName,
						email: response.data.user.email,
						phoneNumber: response.data.user.phoneNumber,
						selectedRole: response.data.user.roleId,
						selectedCustomer: response.data.user.customer?.id || '',
						selectedCustomers: response.data.user.customers?.map((el) => el.id) || [],
						selectedRestaurants: response.data.user.restaurants?.map((el) => el.id) || [],
						filePath: response.data.user.filePath,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditUserActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: EditUserActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditUserActionTypes.DELETE_PHOTO }), []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditUserActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeSurName = useCallback((value: string) => {
		dispatch({
			type: EditUserActionTypes.CHANGE_SURNAME,
			payload: {
				surName: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: EditUserActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changePhoneNumber = useCallback((value: string) => {
		dispatch({
			type: EditUserActionTypes.CHANGE_PHONE_NUMBER,
			payload: {
				phoneNumber: value,
			},
		});
	}, []);

	const setFetchedRoles = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditUserActionTypes.SET_FETCHED_ROLES,
			payload: {
				roles: value,
			},
		});
	}, []);

	const setRole = useCallback((value: TEditUserSelectedItem) => {
		dispatch({
			type: EditUserActionTypes.SET_ROLE,
			payload: {
				role: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditUserActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: TEditUserSelectedItem | null) => {
		dispatch({
			type: EditUserActionTypes.SET_CUSTOMER,
			payload: {
				selectedCustomer: value,
			},
		});
	}, []);

	const setCustomers = useCallback((value: string[]) => {
		dispatch({
			type: EditUserActionTypes.SET_CUSTOMERS,
			payload: {
				selectedCustomers: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditUserActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditUserActionTypes.SET_RESTAURANTS,
			payload: {
				selectedRestaurants: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IUpdateUser | null) => {
		dispatch({
			type: EditUserActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditUserModalWindow) => {
		dispatch({
			type: EditUserActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IUserEmailValidationError> | null) => {
		dispatch({
			type: EditUserActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditUserActionTypes.RESET_FIELDS }), []);

	return (
		<EditUserContext.Provider
			value={{
				refetchData,
				setFile,
				deletePhoto,
				changeName,
				changeSurName,
				changeEmail,
				changePhoneNumber,
				setFetchedRoles,
				setRole,
				setFetchedCustomers,
				setCustomer,
				setCustomers,
				setFetchedRestaurants,
				setRestaurants,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				userID: state.userID,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				filePath: state.filePath,
				name: state.name,
				surName: state.surName,
				email: state.email,
				phoneNumber: state.phoneNumber,
				fetchedRoles: state.fetchedRoles,
				selectedRole: state.selectedRole,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				selectedCustomers: state.selectedCustomers,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditUserContext.Provider>
	);
};

export default EditUserState;
